/**
 * threeR186 — two three.js r186 fixes our r185.1 lacks, backported (the shader sweep, 2026-10-06). Each stands down at
 * three r186+ (where three does it itself), and each is a no-op when the thing it patches is missing.
 *
 * 1. THE PRECOMPILE SHADOW RENDER (three #33924, issue #33898). r185.1's `compileAsync` walks its queued objects one by
 *    one and calls `NodeManager.updateBefore(renderObject)` for each — which reaches `ShadowNode.updateBefore`, and that
 *    RENDERS the light's shadow map (every CSM cascade: a whole-scene render per cascade) whenever its per-camera frame
 *    stamp is stale. `compileAsync` itself bumps the node frame id on entry, so the stamp is always stale for the first
 *    shadow-receiving object of every compile. Atlas calls `compileAsync` constantly while the world streams (every
 *    instancer batch, terrain chunk, dressing group, creature — 84 calls in one 7-minute ultra boot): each was an extra
 *    synchronous all-cascade shadow render on the main thread, plus a one-frame glitch (#33898). r186 guards the shadow
 *    render with a renderer flag set only around the loop's synchronous `updateBefore` / `updateAfter` calls; the
 *    backport tells the loop apart from a real render by the render context (a render pass always has one; the compile
 *    loop runs between renders with none) while a `compileAsync` is in flight.
 *
 * 2. THE LATE CASCADES (three #34213). r185.1's `CSMShadowNode.updateBefore` moves each cascade's light and target but
 *    never updates their world matrices, and nothing orders it before the cascades' own `ShadowNode.updateBefore` — so a
 *    cascade re-render used LAST frame's placement (shadows trailing the camera by a frame, cascade edges cutting at
 *    speed). r186 registers the CSM node as a sequential node (its `updateBefore` first) and updates the matrices. The
 *    class lives in three's examples, so the caller hands it in (`backportCsmShadowNode(CSMShadowNode)`).
 */
import * as THREE from 'three/webgpu';

const REVISION = Number.parseInt(String((THREE as unknown as { REVISION?: string }).REVISION ?? '0'), 10);
/** three fixed both in r186 (#33924, #34213). */
export const THREE_FIXES_R186_BACKPORTS = REVISION >= 186;

interface CompilingRenderer {
  compileAsync?: (...args: unknown[]) => Promise<unknown>;
  _currentRenderContext?: unknown;
  __enginexCompiling?: number;
  __enginexPrecompileGuard?: boolean;
}

/**
 * True when a shadow node's `updateBefore` is being called by `compileAsync`'s per-object loop rather than a render:
 * a compile is in flight and no render pass is active (pure — pinned by the engine test).
 */
export function inPrecompileLoop(renderer: CompilingRenderer | null | undefined): boolean {
  return !!renderer && (renderer.__enginexCompiling ?? 0) > 0 && (renderer._currentRenderContext === null || renderer._currentRenderContext === undefined);
}

type UpdateBefore = (this: unknown, frame: { renderer?: CompilingRenderer } | undefined) => unknown;

/** Patch a node class's `updateBefore` to skip the precompile loop (idempotent). */
function guardUpdateBefore(proto: { updateBefore?: UpdateBefore; __enginexPrecompileGuard?: boolean } | undefined): boolean {
  if (!proto || typeof proto.updateBefore !== 'function' || proto.__enginexPrecompileGuard) return false;
  const orig = proto.updateBefore;
  proto.updateBefore = function (this: unknown, frame) {
    // three #33924: compileAsync must warm pipelines, never render shadow maps (a whole-scene render per cascade).
    if (inPrecompileLoop(frame?.renderer)) return undefined;
    return orig.call(this, frame);
  };
  proto.__enginexPrecompileGuard = true;
  return true;
}

/** Install the precompile shadow guard on a renderer (no-op at three r186+). Returns whether it is active. */
export function installPrecompileShadowGuard(renderer: unknown): boolean {
  if (THREE_FIXES_R186_BACKPORTS) return false;
  const r = renderer as CompilingRenderer | null;
  if (!r || typeof r.compileAsync !== 'function') return false;
  // A strict test mock of three/webgpu THROWS on an export it lacks — a missing class only stands the guard down.
  let shadowProto: Parameters<typeof guardUpdateBefore>[0];
  try { shadowProto = (THREE as unknown as { ShadowNode?: { prototype: Parameters<typeof guardUpdateBefore>[0] } }).ShadowNode?.prototype; } catch { shadowProto = undefined; }
  if (!shadowProto) return false;
  guardUpdateBefore(shadowProto);
  if (r.__enginexPrecompileGuard) return true;
  r.__enginexPrecompileGuard = true;
  r.__enginexCompiling = 0;
  const orig = r.compileAsync.bind(r);
  r.compileAsync = async (...args: unknown[]) => {
    r.__enginexCompiling = (r.__enginexCompiling ?? 0) + 1;
    try {
      return await orig(...args);
    } finally {
      r.__enginexCompiling = Math.max(0, (r.__enginexCompiling ?? 1) - 1);
    }
  };
  return true;
}

interface CsmLike {
  lights?: Array<{ updateMatrixWorld?: () => void; target?: { updateMatrixWorld?: () => void } }>;
}

/**
 * Backport three #34213 onto `CSMShadowNode` (pass the class — it lives in three's examples). No-op at r186+.
 * Returns whether it patched.
 */
export function backportCsmShadowNode(CSMShadowNodeClass: { prototype: object } | null | undefined): boolean {
  if (THREE_FIXES_R186_BACKPORTS) return false;
  const proto = CSMShadowNodeClass?.prototype as (Record<string, unknown> & { __enginexCsmBackport?: boolean }) | undefined;
  if (!proto || proto.__enginexCsmBackport || typeof proto.updateBefore !== 'function') return false;
  proto.__enginexCsmBackport = true;
  if (!Object.prototype.hasOwnProperty.call(proto, 'build')) {
    const parentBuild = (Object.getPrototypeOf(proto) as { build?: (...a: unknown[]) => unknown }).build;
    if (typeof parentBuild === 'function') {
      proto.build = function (this: unknown, builder: { addSequentialNode?: (n: unknown) => void } | undefined, ...rest: unknown[]) {
        // three #34213: register first, so this node's updateBefore places the cascades before they render.
        builder?.addSequentialNode?.(this);
        return parentBuild.call(this, builder, ...rest);
      };
    }
  }
  const origUpdate = proto.updateBefore as (this: CsmLike, ...a: unknown[]) => unknown;
  proto.updateBefore = function (this: CsmLike, ...a: unknown[]) {
    const out = origUpdate.apply(this, a);
    // three #34213: the cascades' matrices follow the positions just set (they were a frame late).
    for (const lw of this.lights ?? []) { lw.updateMatrixWorld?.(); lw.target?.updateMatrixWorld?.(); }
    return out;
  };
  return true;
}
